import { definedMap, panic, proveNever } from "@glideapps/ts-necessities";
import md5 from "blueimp-md5";
import deepEqual from "deep-equal";
import last from "lodash/last";

import { BasePrimitiveValue } from "../../components/computation-types";
import { TableName } from "../../database-strings";
import { logError, logTrace } from "../../debug-print";
import { assert, assertNever, checkString, defined } from "../../Support";
import { FetchingBuilder, SchemaInspector, UserProfileRowBuild } from "../components";
import {
    BinaryMathFunction,
    BinaryPredicateFormulaOperator,
    ColumnType,
    ColumnTypeKind,
    Formula,
    FormulaKind,
    PrimitiveGlideType,
    PrimitiveGlideTypeKind,
    QueryParametersFormulas,
    ReduceOperator,
    ReduceToMemberByOperator,
    SpecialValueKind,
    TableColumn,
    TableGlideType,
    UnaryMathFunction,
    UnaryPredicateFormulaOperator,
    decomposeRelationType,
    findTable,
    getTableColumn,
    getTableName,
    getTableRefTableName,
    isComputedColumn,
    isDateOrDateTimeTypeKind,
    isMultiRelationType,
    isNumberTypeKind,
    isPrimitiveType,
    isSingleRelationType,
    makeArrayType,
    makeTableRef,
    specialValueTypeKinds,
} from "../description";
import { resolveSourceColumn } from "../source-column";
import {
    ArraySourceSpecification,
    ColumnOrValueKind,
    ColumnOrValueSpecification,
    RollupKind,
    SyntheticColumnKind,
    decomposeAll,
    displayFormulaInputVariableName,
    isGetDisplayFormulaInput,
} from "./formula-specifications";

type FormulaTypeKind = ColumnTypeKind | "lat-long";
type FormulaType = ColumnType | { readonly kind: "lat-long" };

export function isPrimitiveFormulaType(t: FormulaType | undefined): t is PrimitiveGlideType {
    if (t === undefined) return false;
    return t.kind !== "lat-long" && isPrimitiveType(t);
}

export function isNumberFormulaTypeKind(kind: FormulaTypeKind | undefined): kind is "number" | "duration" {
    return kind !== undefined && kind !== "lat-long" && isNumberTypeKind(kind);
}

export function isDateOrDateTimeFormulaType(t: FormulaType) {
    if (t.kind === "lat-long") return false;
    return isDateOrDateTimeTypeKind(t.kind);
}

interface SuccessCompileResult<T> {
    readonly success: true;

    readonly value: T;
    readonly type: ColumnType | undefined;
    readonly typeIsPrecise: boolean;
    readonly geocodeQuotaKeys: ReadonlySet<string>;
}

interface ErrorCompileResult {
    readonly success: false;

    readonly issues: readonly string[];
    readonly errors: readonly string[];
}

export type CompileResult<T> = SuccessCompileResult<T> | ErrorCompileResult;

export interface InternalCompileResult<T> {
    readonly value: T;
    readonly type: FormulaType | undefined;
    readonly typeIsPrecise: boolean;
}

export interface CodeGenerator<T, P> {
    readonly inlinedColumnFormats: boolean;

    readonly issues: readonly string[];

    readonly mappedRowProps: P;
    getPropsFromResult(result: T): P;

    makeVariableName(name: string): string;
    makeContextVariableName(contextName: string | undefined): string;

    // `null` is used for `Empty`
    constant(value: BasePrimitiveValue | null): T;
    getColumn(column: TableColumn, table: TableGlideType, row: T, forDataEditor: boolean): T | undefined;
    inContext(variable: string, body: InternalCompileResult<T>): T;
    getContextVariable(name: string, isInner: boolean, props: P): T;
    orderComparison?(builtin: string, left: InternalCompileResult<T>, right: InternalCompileResult<T>): T;
    withDefaultFalse(value: InternalCompileResult<T>): T;
    fetchTableRows?(table: TableName, forDataEditor: boolean): T;
    filterRows?(rows: InternalCompileResult<T>, predicate: InternalCompileResult<T>): T;
    mapRows?(rows: InternalCompileResult<T>, fn: InternalCompileResult<T>): T;
    reduce?(op: ReduceOperator, rows: InternalCompileResult<T>, value: InternalCompileResult<T>): T;
    reduceToMemberBy?(
        op: ReduceToMemberByOperator,
        list: InternalCompileResult<T>,
        mapper: InternalCompileResult<T>,
        result: InternalCompileResult<T>
    ): T;
    joinStrings?(
        list: InternalCompileResult<T>,
        mapper: InternalCompileResult<T>,
        separator: InternalCompileResult<T>
    ): T;
    splitString(string: InternalCompileResult<T>, separator: InternalCompileResult<T>): T;
    findRow?(list: InternalCompileResult<T>, predicate: InternalCompileResult<T>): T;
    ifThenElse(
        condition: InternalCompileResult<T>,
        consequent: InternalCompileResult<T>,
        alternative: InternalCompileResult<T> | undefined
    ): T;
    checkValue(op: UnaryPredicateFormulaOperator, value: InternalCompileResult<T>): T;
    compareValues(
        op: BinaryPredicateFormulaOperator,
        left: InternalCompileResult<T>,
        right: InternalCompileResult<T>
    ): T | undefined;
    getUserProfileRow?(userProfile: UserProfileRowBuild): T;
    applyColumnFormat(value: InternalCompileResult<T>, table: TableGlideType, column: TableColumn): T | undefined;
    constantTextTemplate?(template: string, replacements: ReadonlyMap<string, T>): T;
    textTemplate?(template: InternalCompileResult<T>, replacements: readonly T[]): T;
    arrayContains?(array: InternalCompileResult<T>, item: InternalCompileResult<T>): T;
    arraysOverlap?(left: InternalCompileResult<T>, right: InternalCompileResult<T>): T;
    and(left: InternalCompileResult<T>, right: InternalCompileResult<T>): T;
    or?(left: InternalCompileResult<T>, right: InternalCompileResult<T>): T;
    not(value: InternalCompileResult<T>): T;
    specialValue?(kind: SpecialValueKind): T;
    convertToType?(value: InternalCompileResult<T>, typeKind: PrimitiveGlideTypeKind): T | undefined;
    startOfDay?(dateTime: InternalCompileResult<T>): T;
    endOfDay?(dateTime: InternalCompileResult<T>): T;
    with(value: InternalCompileResult<T>, context: InternalCompileResult<T>): T;
    getNth?(list: InternalCompileResult<T>, index: InternalCompileResult<T>): T;
    getNthLast?(list: InternalCompileResult<T>, index: InternalCompileResult<T>): T;
    randomPick?(list: InternalCompileResult<T>, uniqueID: string): T;
    isInRange?(value: InternalCompileResult<T>, start: InternalCompileResult<T>, end: InternalCompileResult<T>): T;
    assignVariables(lets: readonly [string, InternalCompileResult<T>][], body: InternalCompileResult<T>): T;
    getVariable(name: string, binding: InternalCompileResult<T>): T;
    randomMath?(): T;
    unaryMath?(op: UnaryMathFunction, operand: InternalCompileResult<T>): T;
    binaryMath(
        op: BinaryMathFunction,
        left: InternalCompileResult<T>,
        leftIsConstant: boolean,
        right: InternalCompileResult<T>,
        rightIsConstant: boolean
    ): T | undefined;
    constantFormatNumberFixed?(
        value: InternalCompileResult<T>,
        decimalsAfterPoint: number,
        groupSeparator: boolean,
        currency: string | undefined
    ): T;
    subtractDates?(left: InternalCompileResult<T>, right: InternalCompileResult<T>): T;
    addDurationToDate?(date: InternalCompileResult<T>, duration: InternalCompileResult<T>, negate: boolean): T;
    formatNumberFixed(
        value: InternalCompileResult<T>,
        decimalsAfterPoint: InternalCompileResult<T>,
        groupSeparator: InternalCompileResult<T>,
        currency: InternalCompileResult<T> | undefined
    ): T;
    formatDateTime?(
        value: InternalCompileResult<T>,
        dateFormat: InternalCompileResult<T> | undefined,
        timeFormat: InternalCompileResult<T> | undefined
    ): T;
    currentLocation?(): T;
    geocodeAddress?(value: InternalCompileResult<T>, quotaKey: string): T;
    geoDistance?(left: InternalCompileResult<T>, right: InternalCompileResult<T>): T;
    generateImage?(input: InternalCompileResult<T>, imageKind: InternalCompileResult<T>): T;
    userAPIFetch?(webhookID: string, params: readonly T[]): T;
    constructURL?(
        scheme: InternalCompileResult<T>,
        host: InternalCompileResult<T>,
        path: InternalCompileResult<T>,
        params: readonly T[]
    ): T;
    yesCodeFetch?(
        url: InternalCompileResult<T>,
        params: readonly [InternalCompileResult<T>, InternalCompileResult<T>][]
    ): T;
}

const reduceType: Record<ReduceOperator, PrimitiveGlideTypeKind> = {
    [ReduceOperator.AllTrue]: "boolean",
    [ReduceOperator.SomeTrue]: "boolean",
    [ReduceOperator.Sum]: "number",
    [ReduceOperator.CountNonEmpty]: "number",
    [ReduceOperator.CountUnique]: "number",
    [ReduceOperator.CountTrue]: "number",
    [ReduceOperator.CountNotTrue]: "number",
    [ReduceOperator.Average]: "number",
    [ReduceOperator.Range]: "number",
};

type Context<P> = [string, TableGlideType | PrimitiveGlideTypeKind, P];

export type InitialFormulaBinding<T> = [string, InternalCompileResult<T>];
// [context name, table, variable name, props]
export type BoundContext<P> = [string, TableName, string, P];

export function makeQuotaKeyForFormula(f: unknown): string {
    return `formula:${md5(JSON.stringify(f))}`;
}

// Will return the compiled formula, or a list of issues.
export function compileFormulaWithCodeGenerator<T, P>(
    codegen: CodeGenerator<T, P>,
    builder: FetchingBuilder,
    outerTable: TableGlideType | undefined,
    outerProps: P | undefined,
    formula: Formula,
    forDataEditor: boolean,
    uniqueID: string,
    initialBindings?: readonly InitialFormulaBinding<T>[],
    boundContexts?: readonly BoundContext<P>[]
): CompileResult<T> {
    const { schema } = builder;

    const issues: string[] = [];
    const errors: string[] = [];
    const geocodeQuotaKeys = new Set<string>();

    // FIXME: This can be simplified.  This array should never be empty.
    // The first item should be the outermost context.
    const contexts: Context<P>[] = [];

    // Pushes and pops from the front
    const bindings: ReadonlyMap<string, InternalCompileResult<T>>[] = [];

    function inContext(
        contextName: string,
        contextType: TableGlideType | PrimitiveGlideTypeKind,
        contextProps: P,
        f: () => InternalCompileResult<T> | undefined
    ): InternalCompileResult<T> | undefined {
        assert(contexts.findIndex(([n]) => n === contextName) < 0);

        contexts.push([contextName, contextType, contextProps]);
        const r = f();
        contexts.pop();

        if (r === undefined) return undefined;
        const variable = codegen.makeContextVariableName(last(contexts)?.[0]);
        return { value: codegen.inContext(variable, r), type: r.type, typeIsPrecise: r.typeIsPrecise };
    }

    function failWithIssue(issue: string): undefined {
        issues.push(issue);
        return undefined;
    }

    function failWithError(error: string): undefined {
        errors.push(error);
        return failWithIssue(error);
    }

    function getContext(
        contextName: string | undefined
    ): { type: TableGlideType | PrimitiveGlideTypeKind; value: T } | undefined {
        if (contextName === undefined) {
            if (outerTable === undefined || outerProps === undefined) {
                return failWithIssue(`No table given`);
            }
            return {
                type: outerTable,
                value: codegen.getContextVariable(
                    codegen.makeContextVariableName(undefined),
                    contexts.length === 0,
                    outerProps
                ),
            };
        }

        const entry = contexts.find(([n]) => n === contextName);
        if (entry !== undefined) {
            return {
                type: entry[1],
                value: codegen.getContextVariable(
                    codegen.makeContextVariableName(entry[0]),
                    entry === last(contexts),
                    entry[2]
                ),
            };
        }

        const boundEntry = boundContexts?.find(([n]) => n === contextName);
        if (boundEntry !== undefined) {
            const [, tableName, varName, props] = boundEntry;
            const table = findTable(schema, tableName);
            if (table === undefined) {
                return failWithIssue(`Table ${tableName.name} does not exist`);
            }
            return {
                type: table,
                value: codegen.getContextVariable(varName, false, props),
            };
        }

        return failWithIssue(`Context ${contextName} is not defined`);
    }

    function getTable(t: FormulaType): TableGlideType | undefined {
        if (t.kind === "lat-long" || !isSingleRelationType(t)) {
            return failWithIssue(`Expected table ref type but got ${t.kind}`);
        }
        const table = findTable(schema, t);
        if (table === undefined) {
            issues.push(`Table ${getTableRefTableName(t).name} does not exist`);
        }
        return table;
    }

    function getArrayTableOrPrimitive(t: FormulaType): TableGlideType | PrimitiveGlideTypeKind | undefined {
        if (t.kind !== "array") {
            return failWithIssue(`Expected array type but got ${t.kind}`);
        }
        if (isPrimitiveType(t.items)) {
            return t.items.kind;
        } else {
            return getTable(t.items);
        }
    }

    function getArrayTable(t: FormulaType): TableGlideType | undefined {
        const type = getArrayTableOrPrimitive(t);
        if (type === undefined) return undefined;
        if (typeof type === "string") {
            return failWithIssue(`Expected table ref type but got ${type}`);
        }
        return type;
    }

    function compilePredicate(f: Formula, withDefault: boolean): InternalCompileResult<T> | undefined {
        const r = compile(f);
        if (r === undefined) return undefined;
        if (r.type?.kind !== "boolean") {
            return failWithIssue(`Condition is not boolean but is ${r.type?.kind}`);
        }
        if (!withDefault) {
            return r;
        } else {
            return { ...r, value: codegen.withDefaultFalse(r) };
        }
    }

    function resultWithType(
        value: T | undefined,
        type: FormulaType | undefined,
        typeIsPrecise: boolean
    ): InternalCompileResult<T> | undefined {
        if (value === undefined) {
            if (issues.length === 0) {
                issues.push("Code generator doesn't support formula");
            }
            return undefined;
        }
        return { value, type, typeIsPrecise };
    }

    function primitiveResult(
        value: T | undefined,
        typeKind: PrimitiveGlideTypeKind | "lat-long",
        typeIsPrecise: boolean
    ): InternalCompileResult<T> | undefined {
        return resultWithType(value, { kind: typeKind }, typeIsPrecise);
    }

    function compileQueryParameters(paramsFormulas: QueryParametersFormulas): readonly T[] | undefined {
        const params: T[] = [];
        for (const q of paramsFormulas) {
            const name = compile(q.name);
            const value = compile(q.value);
            if (name === undefined || value === undefined) return undefined;
            if (!isPrimitiveFormulaType(name.type)) {
                return failWithIssue(`Name must be primitive, but is ${name.type?.kind}`);
            }
            if (!isPrimitiveFormulaType(value.type)) {
                return failWithIssue(`Value must be primitive, but is ${value.type?.kind}`);
            }

            params.push(name.value, value.value);
        }
        return params;
    }

    function compile(f: Formula): InternalCompileResult<T> | undefined {
        // Being defensive.  This can happen during development.
        if (f === undefined) {
            logTrace("Formula is undefined");
            return failWithIssue("Internal error: formula is undefined");
        }

        switch (f.kind) {
            case FormulaKind.GetTableRows: {
                const table = findTable(schema, f.table);
                if (table === undefined) {
                    return failWithIssue(`Table ${f.table.name} does not exist`);
                }
                return resultWithType(
                    codegen.fetchTableRows?.(f.table, forDataEditor),
                    makeArrayType(makeTableRef(table)),
                    true
                );
            }
            case FormulaKind.FilterRows: {
                const rows = compile(f.rows);
                if (rows === undefined) return undefined;
                const rowsTable = definedMap(rows.type, getArrayTable);
                if (rowsTable === undefined) return undefined;
                return inContext(f.predicateContextName, rowsTable, codegen.mappedRowProps, () => {
                    const predicate = compilePredicate(f.predicate, false);
                    if (predicate === undefined) return undefined;
                    return resultWithType(codegen.filterRows?.(rows, predicate), rows.type, rows.typeIsPrecise);
                });
            }
            case FormulaKind.MapRows: {
                const rows = compile(f.rows);
                if (rows === undefined) return undefined;
                const rowsTable = definedMap(rows.type, getArrayTable);
                if (rowsTable === undefined) return undefined;
                return inContext(f.functionContextName, rowsTable, codegen.mappedRowProps, () => {
                    const fn = compile(f.function);
                    if (fn?.type === undefined) return undefined;
                    if (fn.type.kind === "array") {
                        return failWithIssue("Cannot map to an array of arrays");
                    }
                    if (fn.type.kind === "lat-long") {
                        return failWithIssue("Cannot map to an array of lat-long");
                    }
                    return resultWithType(codegen.mapRows?.(rows, fn), makeArrayType(fn.type), fn.typeIsPrecise);
                });
            }
            case FormulaKind.Reduce: {
                const rows = compile(f.rows);
                if (rows === undefined) return undefined;
                const itemsType = definedMap(rows.type, getArrayTableOrPrimitive);
                if (itemsType === undefined) return undefined;
                return inContext(f.valueContextName, itemsType, codegen.mappedRowProps, () => {
                    const valueForRow = compile(f.value);
                    if (valueForRow === undefined) return undefined;
                    return primitiveResult(
                        codegen.reduce?.(f.operator, rows, valueForRow),
                        reduceType[f.operator],
                        true
                    );
                });
            }
            case FormulaKind.ReduceToMemberBy: {
                const rows = compile(f.rows);
                if (rows === undefined) return undefined;
                const itemsType = definedMap(rows.type, getArrayTableOrPrimitive);
                if (itemsType === undefined) return undefined;
                return inContext(f.valueContextName, itemsType, codegen.mappedRowProps, () => {
                    const value = compile(f.value);
                    const key = compile(f.key);
                    if (value === undefined || key === undefined) return undefined;
                    return resultWithType(
                        codegen.reduceToMemberBy?.(f.operator, rows, key, value),
                        value.type,
                        value.typeIsPrecise
                    );
                });
            }
            case FormulaKind.JoinStrings: {
                const rows = compile(f.rows);
                if (rows === undefined) return undefined;
                const itemsType = definedMap(rows.type, getArrayTableOrPrimitive);
                if (itemsType === undefined) return undefined;
                const separator = compile(f.separator);
                if (separator === undefined) return undefined;
                return inContext(f.valueContextName, itemsType, codegen.mappedRowProps, () => {
                    const value = compile(f.value);
                    if (value === undefined) return undefined;
                    return primitiveResult(codegen.joinStrings?.(rows, value, separator), "string", true);
                });
            }
            case FormulaKind.SplitString: {
                const string = compile(f.string);
                if (string === undefined) return undefined;
                const separator = compile(f.separator);
                if (separator === undefined) return undefined;
                return resultWithType(
                    codegen.splitString(string, separator),
                    { kind: "array", items: { kind: "string" } },
                    true
                );
            }
            case FormulaKind.FindRow: {
                const rows = compile(f.rows);
                if (rows === undefined) return undefined;
                const rowsTable = definedMap(rows.type, getArrayTable);
                if (rowsTable === undefined) return undefined;
                return inContext(f.predicateContextName, rowsTable, codegen.mappedRowProps, () => {
                    const predicate = compilePredicate(f.predicate, false);
                    if (predicate === undefined) return undefined;
                    return resultWithType(codegen.findRow?.(rows, predicate), makeTableRef(rowsTable), true);
                });
            }
            case FormulaKind.IfThenElse: {
                const condition = compilePredicate(f.condition, true);
                const consequent = compile(f.consequent);
                if (condition === undefined || consequent === undefined) return undefined;
                const alternative = definedMap(f.alternative, compile);
                if (f.alternative !== undefined && alternative === undefined) return undefined;
                let type: FormulaType | undefined;
                let typeIsPrecise: boolean;
                if (alternative === undefined) {
                    type = consequent.type;
                    typeIsPrecise = consequent.typeIsPrecise;
                } else if (deepEqual(consequent.type, alternative.type, { strict: true })) {
                    type = consequent.type;
                    typeIsPrecise = consequent.typeIsPrecise && alternative.typeIsPrecise;
                } else if (consequent.type === undefined && alternative.type === undefined) {
                    typeIsPrecise = consequent.typeIsPrecise && alternative.typeIsPrecise;
                } else if (consequent.type === undefined) {
                    type = alternative.type;
                    typeIsPrecise = false;
                } else if (alternative.type === undefined) {
                    type = consequent.type;
                    typeIsPrecise = false;
                } else if (isPrimitiveFormulaType(consequent.type) && isPrimitiveFormulaType(alternative.type)) {
                    type = { kind: "string" };
                    typeIsPrecise = false;
                } else {
                    return failWithIssue("Incompatible types for if-then-else");
                }
                return resultWithType(codegen.ifThenElse(condition, consequent, alternative), type, typeIsPrecise);
            }
            case FormulaKind.CheckValue: {
                const value = compile(f.value);
                if (value === undefined) return undefined;
                switch (f.operator) {
                    case UnaryPredicateFormulaOperator.IsNotEmpty:
                        return {
                            value: codegen.checkValue(f.operator, value),
                            type: { kind: "boolean" },
                            typeIsPrecise: true,
                        };
                    default:
                        return assertNever(f);
                }
            }
            case FormulaKind.CompareValues: {
                const left = compile(f.left);
                const right = compile(f.right);
                if (left === undefined || right === undefined) return undefined;
                // FIXME: Check that `left` and `right` types can be compared
                const value = codegen.compareValues(f.operator, left, right);
                if (value === undefined) return undefined;
                return { value, type: { kind: "boolean" }, typeIsPrecise: true };
            }
            case FormulaKind.Constant: {
                let type: ColumnType | undefined;
                let typeIsPrecise = true;
                if (typeof f.value === "string") {
                    const lower = f.value.toLowerCase();
                    if (lower === "true" || lower === "false") {
                        type = { kind: "boolean" };
                        typeIsPrecise = false;
                    }
                }
                if (type === undefined) {
                    type = { kind: typeof f.value as PrimitiveGlideTypeKind };
                }
                return { value: codegen.constant(f.value), type, typeIsPrecise };
            }
            case FormulaKind.Empty: {
                return { value: codegen.constant(null), type: undefined, typeIsPrecise: true };
            }
            case FormulaKind.GetContext: {
                const context = getContext(f.contextName);
                if (context === undefined) return undefined;

                if (typeof context.type === "string") {
                    return primitiveResult(context.value, context.type, false);
                } else {
                    return {
                        value: context.value,
                        type: makeTableRef(context.type),
                        typeIsPrecise: true,
                    };
                }
            }
            case FormulaKind.GetColumn: {
                const context = getContext(f.contextName);
                if (context === undefined) return undefined;
                if (typeof context.type === "string") {
                    return failWithIssue("Trying to get a column from a primitive");
                }
                const column = getTableColumn(context.type, f.column);
                if (column === undefined) {
                    return failWithIssue(`Column ${f.column} not found`);
                }

                const tableRef = decomposeRelationType(column.type)?.tableRef;
                if (tableRef !== undefined) {
                    // see `fetchLinkColumn` in `builder.ts`
                    builder.registerTableUsed(getTableRefTableName(tableRef));
                }

                const value = codegen.getColumn(column, context.type, context.value, forDataEditor);
                if (value === undefined) return undefined;
                return {
                    value,
                    type: column.type,
                    typeIsPrecise: isComputedColumn(column) || !isPrimitiveType(column.type),
                };
            }
            case FormulaKind.GetUserProfileRow: {
                const userProfile = builder.makeUserProfileRow();
                if (userProfile === undefined) {
                    return failWithIssue(`No user profile table`);
                }
                return resultWithType(codegen.getUserProfileRow?.(userProfile), makeTableRef(userProfile.table), true);
            }
            case FormulaKind.ApplyColumnFormat: {
                const context = getContext(f.contextName);
                if (context === undefined) return undefined;
                if (typeof context.type === "string") {
                    return failWithIssue("Trying to get a column from a primitive");
                }
                const column = getTableColumn(context.type, f.column);
                if (column === undefined) {
                    return failWithIssue(`Column ${f.column} not found`);
                }
                const value = compile(f.value);
                if (value === undefined) return undefined;

                // Inlined column formats are only supported in the player, because
                // in the builder they can change anytime and are implemented with
                // `WithColumnFormatInBuilder`.
                if (codegen.inlinedColumnFormats && !builder.isBuilder) {
                    const displayFormula = getEffectiveDisplayFormulaForColumn(builder, context.type, column);
                    if (displayFormula === undefined) {
                        return value;
                    }

                    const varName = codegen.makeVariableName(displayFormulaInputVariableName);
                    bindings.unshift(new Map([[displayFormulaInputVariableName, value]]));
                    const compiled = compile(displayFormula);
                    bindings.shift();
                    if (compiled !== undefined) {
                        return primitiveResult(codegen.assignVariables([[varName, value]], compiled), "string", false);
                    }
                }

                return primitiveResult(codegen.applyColumnFormat(value, context.type, column), "string", false);
            }
            case FormulaKind.TextTemplate: {
                const template = compile(f.template);
                if (template === undefined) return undefined;
                if (!isPrimitiveFormulaType(template.type)) {
                    return failWithIssue(`Template must be a primitive, but is ${template.type?.kind}`);
                }
                const replacements: T[] = [];
                const replacementsForStrings = new Map<string, T>();
                let isConstant = true;
                for (const r of f.replacements) {
                    const pattern = compile(r.pattern);
                    const replacement = compile(r.replacement);
                    if (pattern === undefined || replacement === undefined) return undefined;
                    if (!isPrimitiveFormulaType(pattern.type)) {
                        return failWithIssue(`Pattern must be primitive, but is ${pattern.type?.kind}`);
                    }
                    if (!isPrimitiveFormulaType(replacement.type)) {
                        return failWithIssue(`Replacement must be primitive, but is ${replacement.type?.kind}`);
                    }

                    if (r.pattern.kind === FormulaKind.Constant && typeof r.pattern.value === "string") {
                        if (r.pattern.value === "") continue;
                        replacementsForStrings.set(r.pattern.value, replacement.value);
                    } else {
                        isConstant = false;
                    }

                    replacements.push(pattern.value, replacement.value);
                }

                let value: T | undefined;
                if (replacements.length === 0) {
                    // FIXME: Convert to string if the type is not precise
                    value = template.value;
                } else if (
                    isConstant &&
                    f.template.kind === FormulaKind.Constant &&
                    typeof f.template.value === "string"
                ) {
                    value = codegen.constantTextTemplate?.(f.template.value, replacementsForStrings);
                }
                if (value === undefined) {
                    value = codegen.textTemplate?.(template, replacements);
                }

                return primitiveResult(value, "string", true);
            }
            case FormulaKind.ArrayContains: {
                const array = compile(f.array);
                const item = compile(f.item);
                if (array === undefined || item === undefined) return undefined;
                if (array.type?.kind !== "array") {
                    return failWithIssue(`ArrayContains array argument is not an array, but is ${array.type?.kind}`);
                }
                return primitiveResult(codegen.arrayContains?.(array, item), "boolean", true);
            }
            case FormulaKind.ArraysOverlap: {
                const left = compile(f.left);
                const right = compile(f.right);
                if (left === undefined || right === undefined) return undefined;
                if (left.type?.kind !== "array") {
                    return failWithIssue(`ArraysOverlap left argument is not an array, but is ${left.type?.kind}`);
                }
                if (right.type?.kind !== "array") {
                    return failWithIssue(`ArraysOverlap right argument is not an array, but is ${left.type?.kind}`);
                }
                return primitiveResult(codegen.arraysOverlap?.(left, right), "boolean", true);
            }
            case FormulaKind.And:
            case FormulaKind.Or: {
                const left = compile(f.left);
                const right = compile(f.right);
                if (left === undefined || right === undefined) return undefined;
                if (left.type?.kind !== "boolean") {
                    return failWithIssue(`And/Or left argument is not an array, but is ${left.type?.kind}`);
                }
                if (right.type?.kind !== "boolean") {
                    return failWithIssue(`And/Or right argument is not an array, but is ${right.type?.kind}`);
                }
                return primitiveResult(
                    f.kind === FormulaKind.And ? codegen.and(left, right) : codegen.or?.(left, right),
                    "boolean",
                    true
                );
            }
            case FormulaKind.Not: {
                const value = compile(f.value);
                if (value === undefined) return undefined;
                if (value.type?.kind !== "boolean") {
                    return failWithIssue(`Not argument is not a boolean, but is ${value.type?.kind}`);
                }
                return {
                    value: codegen.not(value),
                    type: { kind: "boolean" },
                    typeIsPrecise: true,
                };
            }
            case FormulaKind.SpecialValue:
                return primitiveResult(codegen.specialValue?.(f.valueKind), specialValueTypeKinds[f.valueKind], true);
            case FormulaKind.ConvertToType: {
                const value = compile(f.value);
                if (value === undefined) return undefined;
                if (!isPrimitiveFormulaType(value.type)) {
                    return failWithIssue(`Argument to type conversion is not a primitive, but is ${value.type?.kind}`);
                }
                return primitiveResult(codegen.convertToType?.(value, f.typeKind), f.typeKind, true);
            }
            case FormulaKind.StartOfDay: {
                const dateTime = compile(f.dateTime);
                if (dateTime === undefined) return undefined;
                if (dateTime.type?.kind !== "date-time") {
                    return failWithIssue(`Argument to start of day is not a date-time, but is ${dateTime.type?.kind}`);
                }
                return primitiveResult(codegen.startOfDay?.(dateTime), "date-time", true);
            }
            case FormulaKind.EndOfDay: {
                const dateTime = compile(f.dateTime);
                if (dateTime === undefined) return undefined;
                if (dateTime.type?.kind !== "date-time") {
                    return failWithIssue(`Argument to end of day is not a date-time, but is ${dateTime.type?.kind}`);
                }
                return primitiveResult(codegen.endOfDay?.(dateTime), "date-time", true);
            }
            case FormulaKind.With: {
                const context = compile(f.context);
                if (context?.type === undefined) return undefined;
                const contextTable = getTable(context.type);
                if (contextTable === undefined) return undefined;
                return inContext(f.contextName, contextTable, codegen.getPropsFromResult(context.value), () => {
                    const value = compile(f.value);
                    if (value === undefined) return undefined;
                    return {
                        value: codegen.with(value, context),
                        type: value.type,
                        typeIsPrecise: value.typeIsPrecise,
                    };
                });
            }
            case FormulaKind.GetNth:
            case FormulaKind.GetNthLast: {
                const array = compile(f.array);
                const index = compile(f.index);
                if (array === undefined || index === undefined) return undefined;
                if (array.type?.kind !== "array") {
                    return failWithIssue(`Value to get nth from must be array, but is ${array.type?.kind}`);
                }
                if (!isNumberFormulaTypeKind(index.type?.kind)) {
                    return failWithIssue(`Array index is not a number, but is ${index.type?.kind}`);
                }
                return resultWithType(
                    f.kind === FormulaKind.GetNth ? codegen.getNth?.(array, index) : codegen.getNthLast?.(array, index),
                    array.type.items,
                    array.typeIsPrecise
                );
            }
            case FormulaKind.RandomPick: {
                const array = compile(f.array);
                if (array === undefined) return undefined;
                if (array.type?.kind !== "array") {
                    return failWithIssue(`Value to get random pick from must be array, but is ${array.type?.kind}`);
                }
                return resultWithType(codegen.randomPick?.(array, uniqueID), array.type.items, array.typeIsPrecise);
            }
            case FormulaKind.IsInRange: {
                const value = compile(f.value);
                const start = compile(f.start);
                const end = compile(f.end);
                if (value === undefined || start === undefined || end === undefined) return undefined;
                // FIXME: Check that `value`, `start`, and `end` types can be compared

                return primitiveResult(codegen.isInRange?.(value, start, end), "boolean", true);
            }
            case FormulaKind.AssignVariables: {
                const assignments = new Map<string, InternalCompileResult<T>>();
                const lets: [string, InternalCompileResult<T>][] = [];
                for (const [lhs, rhs] of f.assignments) {
                    const rhsResult = compile(rhs);
                    if (rhsResult === undefined) return undefined;
                    assignments.set(lhs, rhsResult);
                    lets.push([codegen.makeVariableName(lhs), rhsResult]);
                }
                bindings.unshift(assignments);
                const body = compile(f.body);
                bindings.shift();
                if (body === undefined) return undefined;
                return {
                    value: codegen.assignVariables(lets, body),
                    type: body.type,
                    typeIsPrecise: body.typeIsPrecise,
                };
            }
            case FormulaKind.GetVariable: {
                for (const m of bindings) {
                    const binding = m.get(f.name);
                    if (binding === undefined) continue;
                    return {
                        value: codegen.getVariable(codegen.makeVariableName(f.name), binding),
                        type: binding.type,
                        typeIsPrecise: binding.typeIsPrecise,
                    };
                }
                return failWithIssue(`Variable ${f.name} not found`);
            }
            case FormulaKind.Random: {
                return primitiveResult(codegen.randomMath?.(), "number", true);
            }
            case FormulaKind.UnaryMath: {
                const operand = compile(f.operand);
                if (operand === undefined) return undefined;
                if (!isPrimitiveFormulaType(operand.type)) {
                    return failWithIssue(`Math operand must be primitive, but is ${operand.type?.kind}`);
                }
                return primitiveResult(codegen.unaryMath?.(f.fn, operand), "number", true);
            }
            case FormulaKind.BinaryMath: {
                const left = compile(f.left);
                const right = compile(f.right);
                // FIXME: left and right could be more complex expressions that are
                // still constant.
                const leftIsConstant = f.left.kind === FormulaKind.Constant;
                const rightIsConstant = f.right.kind === FormulaKind.Constant;
                if (left === undefined || right === undefined) return undefined;

                if (left.type !== undefined && right.type !== undefined) {
                    const leftIsDate = isDateOrDateTimeFormulaType(left.type);
                    const rightIsDate = isDateOrDateTimeFormulaType(right.type);

                    if (f.fn === BinaryMathFunction.Subtract && leftIsDate && rightIsDate) {
                        return primitiveResult(codegen.subtractDates?.(left, right), "duration", true);
                    } else if (
                        (f.fn === BinaryMathFunction.Add || f.fn === BinaryMathFunction.Subtract) &&
                        leftIsDate &&
                        !rightIsDate
                    ) {
                        return primitiveResult(
                            codegen.addDurationToDate?.(left, right, f.fn === BinaryMathFunction.Subtract),
                            "date-time",
                            true
                        );
                    } else if (leftIsDate || rightIsDate) {
                        return failWithError("Invalid operation on date/time");
                    }
                }

                if (!isPrimitiveFormulaType(left.type) || !isPrimitiveFormulaType(right.type)) {
                    return failWithIssue(
                        `Math operands must be primitives, but they are ${left.type?.kind} and ${right.type?.kind}`
                    );
                }
                const value = codegen.binaryMath(f.fn, left, leftIsConstant, right, rightIsConstant);
                if (value === undefined) return undefined;
                return {
                    value,
                    type: { kind: "number" },
                    typeIsPrecise: true,
                };
            }
            case FormulaKind.WithUserEnteredText: {
                return compile(f.formula);
            }
            case FormulaKind.FormatNumberFixed: {
                const value = compile(f.value);
                if (value === undefined) return undefined;

                if (
                    f.decimalsAfterPoint.kind === FormulaKind.Constant &&
                    typeof f.decimalsAfterPoint.value === "number" &&
                    f.groupSeparator.kind === FormulaKind.Constant &&
                    typeof f.groupSeparator.value === "boolean" &&
                    (f.currency === undefined ||
                        (f.currency.kind === FormulaKind.Constant && typeof f.currency.value === "string")) &&
                    codegen.constantFormatNumberFixed !== undefined
                ) {
                    return primitiveResult(
                        codegen.constantFormatNumberFixed(
                            value,
                            f.decimalsAfterPoint.value,
                            f.groupSeparator.value,
                            definedMap(f.currency?.value, checkString)
                        ),
                        "string",
                        true
                    );
                }

                const decimalsAfterPoint = compile(f.decimalsAfterPoint);
                const groupSeparator = compile(f.groupSeparator);
                if (value === undefined || decimalsAfterPoint === undefined || groupSeparator === undefined) {
                    return undefined;
                }
                let currency: InternalCompileResult<T> | undefined;
                if (f.currency !== undefined) {
                    currency = compile(f.currency);
                    if (currency === undefined) return undefined;
                }
                return primitiveResult(
                    codegen.formatNumberFixed(value, decimalsAfterPoint, groupSeparator, currency),
                    "string",
                    true
                );
            }
            case FormulaKind.FormatDateTime: {
                const value = compile(f.value);
                if (value === undefined) return undefined;
                const dateFormat = definedMap(f.dateFormat, compile);
                if (f.dateFormat !== undefined && dateFormat === undefined) return undefined;
                const timeFormat = definedMap(f.timeFormat, compile);
                if (f.timeFormat !== undefined && timeFormat === undefined) return undefined;

                return primitiveResult(codegen.formatDateTime?.(value, dateFormat, timeFormat), "string", true);
            }
            case FormulaKind.CurrentLocation: {
                return primitiveResult(codegen.currentLocation?.(), "lat-long", true);
            }
            case FormulaKind.GeocodeAddress: {
                const value = compile(f.address);
                if (value === undefined) return undefined;
                if (!isPrimitiveFormulaType(value.type)) {
                    return failWithIssue(`Address must be primitive, but is ${value.type?.kind}`);
                }
                const quotaKey = makeQuotaKeyForFormula(f.address);
                geocodeQuotaKeys.add(quotaKey);
                return primitiveResult(codegen.geocodeAddress?.(value, quotaKey), "lat-long", true);
            }
            case FormulaKind.GeoDistance: {
                const left = compile(f.left);
                const right = compile(f.right);
                if (left === undefined || right === undefined) return undefined;
                if (left.type?.kind !== "lat-long" || right.type?.kind !== "lat-long") {
                    return failWithIssue(`Geo distance operands must be lat/long`);
                }
                if (!left.typeIsPrecise || !right.typeIsPrecise) {
                    return failWithIssue(`Geo distance operands must have a precise type`);
                }
                return primitiveResult(codegen.geoDistance?.(left, right), "number", true);
            }
            case FormulaKind.GenerateImage: {
                const input = compile(f.input);
                const imageKind = compile(f.imageKind);
                if (input === undefined || imageKind === undefined) return undefined;

                return primitiveResult(codegen.generateImage?.(input, imageKind), "image-uri", true);
            }
            case FormulaKind.UserAPIFetch: {
                if (
                    f.webhookID === undefined ||
                    f.webhookID.kind !== FormulaKind.Constant ||
                    typeof f.webhookID.value !== "string"
                ) {
                    return failWithIssue("Webhook ID must be a constant string");
                }
                const params = compileQueryParameters(f.params);
                if (params === undefined) return undefined;

                return primitiveResult(codegen.userAPIFetch?.(f.webhookID.value, params), "string", false);
            }
            case FormulaKind.ConstructURL: {
                const scheme = compile(f.scheme);
                const host = compile(f.host);
                const path = compile(f.path);
                const params = compileQueryParameters(f.params);
                if (scheme === undefined || host === undefined || path === undefined || params === undefined) {
                    return undefined;
                }
                return primitiveResult(codegen.constructURL?.(scheme, host, path, params), "string", true);
            }
            case FormulaKind.YesCode: {
                const url = compile(f.url);
                if (url === undefined) return undefined;
                const params: [InternalCompileResult<T>, InternalCompileResult<T>][] = [];
                for (const param of f.params) {
                    const name = compile(param.name);
                    const value = compile(param.value);
                    if (name === undefined || value === undefined) return undefined;
                    params.push([name, value]);
                }
                return resultWithType(codegen.yesCodeFetch?.(url, params), f.type, true);
            }
            default:
                issues.push(`Unknown formula kind ${(f as any).kind}`);
                return proveNever(f, "Unknown formula kind", undefined);
        }
    }

    // FIXME: Unify this with `FormulaKind.AssignVariables`
    let lets: [string, InternalCompileResult<T>][] | undefined;
    if (initialBindings !== undefined) {
        const assignments = new Map<string, InternalCompileResult<T>>();
        lets = [];
        for (const [lhs, result] of initialBindings) {
            assignments.set(lhs, result);
            lets.push([codegen.makeVariableName(lhs), result]);
        }
        bindings.unshift(assignments);
    }

    let result = compile(formula);

    if (result?.type?.kind === "lat-long") {
        issues.push("Final formula result cannot be lat-long");
        result = undefined;
    }

    if (result === undefined) {
        // logError("Cannot compile formula", issues);
        // logError(JSON.stringify(formula, undefined, 4));
        issues.push(...codegen.issues);
        if (issues.length === 0) {
            compile(formula);
        }
        assert(issues.length > 0);
        return { success: false, issues, errors };
    }

    if (lets !== undefined) {
        bindings.shift();
        result = {
            value: codegen.assignVariables(lets, result),
            type: result.type,
            typeIsPrecise: result.typeIsPrecise,
        };
    }

    return {
        success: true,
        value: result.value,
        type: result.type as ColumnType | undefined,
        typeIsPrecise: result.typeIsPrecise,
        geocodeQuotaKeys,
    };
}

export function canGlobalizeFormula(f: Formula | undefined, ctxs: readonly string[], vars: readonly string[]): boolean {
    if (f === undefined) return true;

    switch (f.kind) {
        case FormulaKind.GetTableRows:
        case FormulaKind.Constant:
        case FormulaKind.Empty:
        case FormulaKind.GetUserProfileRow:
        case FormulaKind.CurrentLocation:
        case FormulaKind.GetContext:
            return true;
        case FormulaKind.FilterRows:
        case FormulaKind.FindRow:
            return (
                canGlobalizeFormula(f.rows, ctxs, vars) &&
                canGlobalizeFormula(f.predicate, [f.predicateContextName, ...ctxs], vars)
            );
        case FormulaKind.MapRows:
            return (
                canGlobalizeFormula(f.rows, ctxs, vars) &&
                canGlobalizeFormula(f.function, [f.functionContextName, ...ctxs], vars)
            );
        case FormulaKind.Reduce:
            return (
                canGlobalizeFormula(f.rows, ctxs, vars) &&
                canGlobalizeFormula(f.value, [f.valueContextName, ...ctxs], vars)
            );
        case FormulaKind.ReduceToMemberBy: {
            const valueCtxs = [f.valueContextName, ...ctxs];
            return (
                canGlobalizeFormula(f.rows, ctxs, vars) &&
                canGlobalizeFormula(f.value, valueCtxs, vars) &&
                canGlobalizeFormula(f.key, valueCtxs, vars)
            );
        }
        case FormulaKind.JoinStrings: {
            return (
                canGlobalizeFormula(f.rows, ctxs, vars) &&
                canGlobalizeFormula(f.value, [f.valueContextName, ...ctxs], vars) &&
                canGlobalizeFormula(f.separator, ctxs, vars)
            );
        }
        case FormulaKind.SplitString: {
            return canGlobalizeFormula(f.string, ctxs, vars) && canGlobalizeFormula(f.separator, ctxs, vars);
        }
        case FormulaKind.IfThenElse:
            return (
                canGlobalizeFormula(f.condition, ctxs, vars) &&
                canGlobalizeFormula(f.consequent, ctxs, vars) &&
                canGlobalizeFormula(f.alternative, ctxs, vars)
            );
        case FormulaKind.CheckValue:
        case FormulaKind.Not:
        case FormulaKind.ConvertToType:
            return canGlobalizeFormula(f.value, ctxs, vars);
        case FormulaKind.CompareValues:
        case FormulaKind.ArraysOverlap:
        case FormulaKind.BinaryMath:
        case FormulaKind.And:
        case FormulaKind.Or:
        case FormulaKind.GeoDistance:
            return canGlobalizeFormula(f.left, ctxs, vars) && canGlobalizeFormula(f.right, ctxs, vars);
        case FormulaKind.GetColumn:
            if (f.contextName === undefined) return false;
            return ctxs.indexOf(f.contextName) >= 0;
        case FormulaKind.ApplyColumnFormat:
        case FormulaKind.SpecialValue:
            // FIXME: Some special values can be global
            return false;
        case FormulaKind.TextTemplate:
            if (!canGlobalizeFormula(f.template, ctxs, vars)) return false;
            for (const { pattern, replacement } of f.replacements) {
                if (!canGlobalizeFormula(pattern, ctxs, vars) || !canGlobalizeFormula(replacement, ctxs, vars)) {
                    return false;
                }
            }
            return true;
        case FormulaKind.ArrayContains:
            return canGlobalizeFormula(f.array, ctxs, vars) && canGlobalizeFormula(f.item, ctxs, vars);
        case FormulaKind.StartOfDay:
        case FormulaKind.EndOfDay:
            return canGlobalizeFormula(f.dateTime, ctxs, vars);
        case FormulaKind.With:
            return (
                canGlobalizeFormula(f.context, ctxs, vars) &&
                canGlobalizeFormula(f.value, [f.contextName, ...ctxs], vars)
            );
        case FormulaKind.GetNth:
        case FormulaKind.GetNthLast:
            return canGlobalizeFormula(f.array, ctxs, vars) && canGlobalizeFormula(f.index, ctxs, vars);
        case FormulaKind.RandomPick:
            return false;
        case FormulaKind.IsInRange:
            return (
                canGlobalizeFormula(f.value, ctxs, vars) &&
                canGlobalizeFormula(f.start, ctxs, vars) &&
                canGlobalizeFormula(f.end, ctxs, vars)
            );
        case FormulaKind.AssignVariables:
            for (const [lhs, rhs] of f.assignments) {
                if (!canGlobalizeFormula(rhs, ctxs, vars)) return false;
                ctxs = [lhs, ...ctxs];
            }
            return canGlobalizeFormula(f.body, ctxs, vars);
        case FormulaKind.Random:
            return false;
        case FormulaKind.UnaryMath:
            return canGlobalizeFormula(f.operand, ctxs, vars);
        case FormulaKind.GetVariable:
            return vars.indexOf(f.name) >= 0;
        case FormulaKind.WithUserEnteredText:
            return canGlobalizeFormula(f.formula, ctxs, vars);
        case FormulaKind.FormatNumberFixed:
            return canGlobalizeFormula(f.value, ctxs, vars) && canGlobalizeFormula(f.decimalsAfterPoint, ctxs, vars);
        case FormulaKind.FormatDateTime:
            if (!canGlobalizeFormula(f.value, ctxs, vars)) return false;
            if (f.dateFormat !== undefined) {
                if (!canGlobalizeFormula(f.dateFormat, ctxs, vars)) return false;
            }
            if (f.timeFormat !== undefined) {
                if (!canGlobalizeFormula(f.timeFormat, ctxs, vars)) return false;
            }
            return true;
        case FormulaKind.GeocodeAddress:
            return canGlobalizeFormula(f.address, ctxs, vars);
        case FormulaKind.GenerateImage:
            return canGlobalizeFormula(f.input, ctxs, vars) && canGlobalizeFormula(f.imageKind, ctxs, vars);
        case FormulaKind.UserAPIFetch:
            for (const { name, value } of f.params) {
                if (!canGlobalizeFormula(name, ctxs, vars) || !canGlobalizeFormula(value, ctxs, vars)) {
                    return false;
                }
            }
            return true;
        case FormulaKind.ConstructURL:
            for (const { name, value } of f.params) {
                if (!canGlobalizeFormula(name, ctxs, vars) || !canGlobalizeFormula(value, ctxs, vars)) {
                    return false;
                }
            }
            return (
                canGlobalizeFormula(f.scheme, ctxs, vars) &&
                canGlobalizeFormula(f.host, ctxs, vars) &&
                canGlobalizeFormula(f.path, ctxs, vars)
            );
        case FormulaKind.YesCode:
            for (const { name, value } of f.params) {
                if (!canGlobalizeFormula(name, ctxs, vars) || !canGlobalizeFormula(value, ctxs, vars)) {
                    return false;
                }
            }
            return canGlobalizeFormula(f.url, ctxs, vars);
        default:
            return assertNever(f);
    }
}

export function hasMathFormulaRandom(f: Formula): boolean {
    switch (f.kind) {
        case FormulaKind.Random:
            return true;
        case FormulaKind.UnaryMath:
            return hasMathFormulaRandom(f.operand);
        case FormulaKind.BinaryMath:
            return hasMathFormulaRandom(f.left) || hasMathFormulaRandom(f.right);
        case FormulaKind.Constant:
        case FormulaKind.GetVariable:
        default:
            return false;
    }
}

const rollupThreadsThroughDisplayFormula: Record<RollupKind, boolean> = {
    [RollupKind.AllTrue]: true,
    [RollupKind.SomeTrue]: true,
    [RollupKind.Sum]: true,
    [RollupKind.CountNonEmpty]: false,
    [RollupKind.Average]: true,
    [RollupKind.Minimum]: true,
    [RollupKind.Maximum]: true,
    [RollupKind.Earliest]: true,
    [RollupKind.Latest]: true,
    [RollupKind.Range]: true,
    [RollupKind.CountNotTrue]: false,
    [RollupKind.CountTrue]: false,
    [RollupKind.CountUnique]: false,
};

export function getColumnForArraySource(
    source: ArraySourceSpecification,
    table: TableGlideType,
    schema: SchemaInspector
): [TableColumn, TableGlideType] | undefined {
    if (source.valueColumn !== undefined) {
        let columnTable: TableGlideType | undefined;
        if (typeof source.tableOrRelationColumn === "string") {
            const column = getTableColumn(table, source.tableOrRelationColumn);
            if (column === undefined) return undefined;
            if (!isMultiRelationType(column.type)) return undefined;
            columnTable = schema.findTable(column.type.items);
        } else {
            columnTable = schema.findTable(source.tableOrRelationColumn);
        }
        if (columnTable === undefined) return undefined;
        const column = getTableColumn(columnTable, source.valueColumn);
        if (column === undefined) return undefined;
        return [column, columnTable];
    } else if (typeof source.tableOrRelationColumn === "string") {
        const column = getTableColumn(table, source.tableOrRelationColumn);
        if (column?.type.kind !== "array") return undefined;
        return [column, table];
    } else {
        return panic("Whole row from table not supported");
    }
}

export function getEffectiveDisplayFormulaForColumn(
    schemaInspector: SchemaInspector,
    table: TableGlideType,
    column: TableColumn,
    // FIXME: This should be internal, not an argument
    visited: Set<TableColumn> = new Set()
): Formula | undefined {
    function getForColumnInTableOrRelation(
        tableOrRelationColumn: TableName | string,
        valueColumnName: string
    ): Formula | undefined {
        let columnTableName: TableName;
        if (typeof tableOrRelationColumn === "string") {
            if (valueColumnName === undefined) return undefined;

            const relationColumn = getTableColumn(defined(table), tableOrRelationColumn);
            if (relationColumn === undefined) return undefined;

            if (!isMultiRelationType(relationColumn.type)) return undefined;

            columnTableName = getTableRefTableName(relationColumn.type.items);
        } else {
            if (valueColumnName === undefined) return undefined;

            columnTableName = tableOrRelationColumn;
        }

        const columnTable = schemaInspector.findTable(columnTableName);
        if (columnTable === undefined) return undefined;

        const valueColumn = getTableColumn(columnTable, valueColumnName);
        if (valueColumn === undefined) return undefined;

        return getEffectiveDisplayFormulaForColumn(schemaInspector, columnTable, valueColumn, visited);
    }

    function getForArraySource(arraySource: ArraySourceSpecification): Formula | undefined {
        if (arraySource.valueColumn === undefined) return undefined;
        const columnAndTable = getColumnForArraySource(arraySource, table, schemaInspector);
        if (columnAndTable === undefined) return undefined;
        return getForColumnInTableOrRelation(getTableName(columnAndTable[1]), columnAndTable[0].name);
    }

    if (visited.has(column)) return undefined;
    visited.add(column);

    const { displayFormula } = column;
    if (displayFormula !== undefined) {
        // This is the dummy display formula
        if (isGetDisplayFormulaInput(displayFormula)) return undefined;
        return displayFormula;
    }

    if (column.formula === undefined) return undefined;
    const spec = decomposeAll(column.formula);
    if (spec === undefined) return undefined;

    switch (spec.kind) {
        case SyntheticColumnKind.Rollup:
            if (rollupThreadsThroughDisplayFormula[spec.rollupKind] !== true) return undefined;
            return getForArraySource(spec.arraySource);

        case SyntheticColumnKind.SingleValue:
            if (spec.arraySource.valueColumn === undefined) return undefined;
            return getForArraySource(spec.arraySource);

        case SyntheticColumnKind.Lookup:
            return getForColumnInTableOrRelation(spec.tableOrRelationColumn, spec.columnInRelation);

        case SyntheticColumnKind.IfThenElse: {
            let format: Formula | undefined;

            function processValue(v: ColumnOrValueSpecification<string | number>): boolean {
                if (v.kind === ColumnOrValueKind.Constant) {
                    return v.value === "";
                }
                if (v.kind === ColumnOrValueKind.Empty) {
                    return true;
                }
                if (v.kind !== ColumnOrValueKind.Column) {
                    logError("Unknown value kind for if-then-else", v.kind);
                    return false;
                }

                const resolved = definedMap(resolveSourceColumn(schemaInspector, v.column, table, undefined), last);
                if (resolved === undefined) return true;

                const f = getEffectiveDisplayFormulaForColumn(
                    schemaInspector,
                    resolved.table,
                    resolved.column,
                    visited
                );
                if (f === undefined) return false;

                if (format === undefined) {
                    format = f;
                } else {
                    if (!deepEqual(format, f, { strict: true })) return false;
                }

                return true;
            }

            for (const clause of spec.clauses) {
                if (!processValue(clause.thenValue)) return undefined;
            }
            if (!processValue(spec.elseValue)) return undefined;
            return format;
        }

        default:
            return undefined;
    }
}

export function makeUniqueIDForColumnFormula(tableName: TableName, columnName: string): string {
    return md5(`${tableName.name}-${columnName}`);
}
